/**
 * Firestore adapter for `structures/{id}`: the only module that knows this
 * collection's documents, snapshots and transactions. The repository above it
 * knows operations, pending fields and listeners; nothing above that knows
 * Firebase at all.
 *
 * Three verbs:
 *   readStructure    one read (optionally from the server, past the cache)
 *   watchStructure   the document, live, with `hasPendingWrites` so the
 *                    repository can tell its own echo from a remote write
 *   applyStructureOps the OPERATION writer: last write wins per element,
 *                    untouched elements as the server has them, an element
 *                    the server no longer has skipped rather than re-created.
 *                    The transaction reads what the server holds NOW; the
 *                    caller's copy is never written. This is the writer
 *                    `services/structureService.updateStructureOpeningOps`
 *                    delegates to.
 *
 * Every document crosses `parseStructureDoc` on the way in: `safeParse`,
 * because a scan that fails the schema must be REPORTED, not blanked — the
 * raw record is passed through with the issues logged so `hostIssues` sees
 * them.
 */
import {
  doc,
  getDoc,
  getDocFromServer,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import type { DocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../client';
import { stripUndefined } from '../../../utils/firestoreSafe';
import { setPath } from '../../repositories/documentPaths';
import { applyOpeningOps, isEmptyOps } from '../../../utils/openingArrayOps';
import {
  STRUCTURE_ARRAY_KEYS,
  StructureDocSchema,
} from '../../schemas/structure.schema';
import type {
  ApplyOpsResult,
  StructureArrayKey,
  StructureDoc,
  StructureOpsByKey,
  StructurePatch,
  StructurePatchFields,
  StructureWrite,
} from '../../types/structure.types';

export interface StructureSnapshot {
  doc: StructureDoc;
  /** True for the SDK's optimistic echo of this client's own write. */
  hasPendingWrites: boolean;
}

const structureRef = (structureId: string) =>
  doc(db, 'structures', structureId);

/**
 * The stored fields under the record's OWN id (`{ ...data, id }`, in that
 * order: a few records carry a stray `id` field, and a project copy once
 * carried its source's — read the other way round, the copy opened as the
 * original). Then the schema.
 */
const schemaFailuresReported = new Set<string>();
/** For tests: forget which documents have been reported. */
export const __resetSchemaReportsForTests = () =>
  schemaFailuresReported.clear();

export const parseStructureDoc = (
  structureId: string,
  data: DocumentData | undefined
): StructureDoc => {
  const raw = { ...(data || {}), id: structureId };
  const parsed = StructureDocSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  // Reported ONCE per document per page. The document is live, so the same
  // failure would otherwise log on every snapshot — and `hostIssues` forwards
  // every console.error from the iOS WebView, dedupes by message, and caps
  // reports per page: a steady repeat is noise in the console and no new
  // information.
  if (!schemaFailuresReported.has(structureId)) {
    schemaFailuresReported.add(structureId);
    console.error(
      `[structures.adapter] structures/${structureId} does not match the structure schema; passing the raw record through`,
      parsed.error.issues.slice(0, 5)
    );
  }
  return raw as unknown as StructureDoc;
};

const fromSnapshot = (
  structureId: string,
  snap: DocumentSnapshot
): StructureDoc | null =>
  snap.exists() ? parseStructureDoc(structureId, snap.data()) : null;

export async function readStructure(
  structureId: string,
  { fromServer = false }: { fromServer?: boolean } = {}
): Promise<StructureDoc | null> {
  const ref = structureRef(structureId);
  const snap = fromServer ? await getDocFromServer(ref) : await getDoc(ref);
  return fromSnapshot(structureId, snap);
}

export function watchStructure(
  structureId: string,
  onNext: (snapshot: StructureSnapshot) => void,
  onError: (error: Error) => void
): () => void {
  return onSnapshot(
    structureRef(structureId),
    (snap) => {
      const parsed = fromSnapshot(structureId, snap);
      if (!parsed) {
        onError(new Error(`Structure ${structureId} not found`));
        return;
      }
      onNext({ doc: parsed, hasPendingWrites: snap.metadata.hasPendingWrites });
    },
    (error) => onError(error)
  );
}

/** The keys of an op set that carry anything. */
export const opsKeys = (ops: StructureOpsByKey): StructureArrayKey[] =>
  STRUCTURE_ARRAY_KEYS.filter((key) => !isEmptyOps(ops?.[key]));

const topLevel = (path: string): string => path.split('.')[0];

/** The top-level document keys a write touches: its arrays and its patch's. */
export const writeKeys = (write: StructureWrite): string[] => {
  const keys = new Set<string>(opsKeys(write.ops || {}));
  const patch = write.patch;
  if (patch) {
    const names =
      typeof (patch as { fields?: unknown }).fields === 'function'
        ? (patch as { keys: string[] }).keys
        : Object.keys(patch as StructurePatchFields);
    for (const name of names) keys.add(topLevel(name));
  }
  return [...keys];
};

/**
 * The fields a patch sets, computed against `doc` (the document as it will
 * be after the ops), and `doc` with them applied — the view the NEXT patch
 * of a batch is computed against.
 */
export function resolvePatch(
  patch: StructurePatch,
  doc: Record<string, unknown>
): { fields: StructurePatchFields; doc: Record<string, unknown> } {
  const isFn = typeof (patch as { fields?: unknown }).fields === 'function';
  const fields = isFn
    ? (
        patch as {
          fields: (d: Record<string, unknown>) => StructurePatchFields;
        }
      ).fields(doc)
    : (patch as StructurePatchFields);
  // A function patch names its keys up front so the session marks them
  // pending; a field it writes but did not name is not protected from an
  // incoming snapshot. Dev-only, because the keys are a static promise.
  if (isFn && process.env.NODE_ENV !== 'production') {
    const named = new Set((patch as { keys: string[] }).keys);
    for (const path of Object.keys(fields)) {
      if (!named.has(topLevel(path))) {
        console.error(
          `[structures] a function patch wrote "${path}" but named only ${[...named].join(', ')}`
        );
      }
    }
  }
  let next = doc;
  for (const [path, value] of Object.entries(fields)) {
    next = setPath(next, path, value);
  }
  return { fields, doc: next };
}

/**
 * Apply one write, or SEVERAL in order, in one transaction. A list is what
 * the session's per-document queue hands over: the writes a burst of edits
 * queued behind an in-flight one, composed here — each op set applied to
 * the array the previous one produced (never merged key by key, so a remove
 * followed by an add of the same id keeps its meaning), each patch computed
 * against the document as the ops and the patches before it left it.
 */
export async function applyStructureWrite(
  structureId: string,
  writeOrList: StructureWrite | StructureWrite[]
): Promise<ApplyOpsResult> {
  const list = Array.isArray(writeOrList) ? writeOrList : [writeOrList];
  const arrayKeys = STRUCTURE_ARRAY_KEYS.filter((key) =>
    list.some((w) => !isEmptyOps(w.ops?.[key]))
  );
  const hasPatch = list.some((w) => w.patch);
  if (!arrayKeys.length && !hasPatch) return { skipped: [] };

  const ref = structureRef(structureId);
  let skipped: string[] = [];
  let written: Record<string, unknown> = {};
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Structure ${structureId} not found`);
    }
    const serverData = (snap.data() || {}) as Record<string, unknown>;
    const updates: Record<string, unknown> = {};
    skipped = [];
    for (const key of arrayKeys) {
      let array = serverData[key] as unknown[];
      for (const w of list) {
        if (isEmptyOps(w.ops?.[key])) continue;
        const applied = applyOpeningOps(array, w.ops![key]!);
        array = applied.array;
        for (const id of applied.skippedUpsertIds) {
          const tag = `${key}:${id}`;
          if (!skipped.includes(tag)) skipped.push(tag);
        }
      }
      updates[key] = array;
    }
    // Patches see the document as the ops left it — a ledger reconciled
    // against the walls that will be written, not the ones that were.
    let view: Record<string, unknown> = { ...serverData, ...updates };
    for (const w of list) {
      if (!w.patch) continue;
      const resolved = resolvePatch(w.patch, view);
      Object.assign(updates, resolved.fields);
      view = resolved.doc;
    }
    // `updateDoc` rejects `undefined` anywhere in the payload (an unset
    // miter, say).
    const payload = stripUndefined(updates) as Record<string, unknown>;
    tx.update(ref, {
      ...payload,
      // Stamped like the room writer stamps a room: who, and when.
      lastModified: serverTimestamp(),
      modifiedBy: 'user',
    } as DocumentData);
    // Assigned inside: a retried transaction re-reads and re-applies, and the
    // last attempt is the one that committed. The stamp is the server's to
    // report, never settled from here.
    written = payload;
  });

  if (skipped.length) {
    // Not an error: someone deleted it, and an edit must not resurrect a
    // deleted element.
    console.warn(
      'Structure edit skipped elements deleted elsewhere:',
      skipped.join(', ')
    );
  }
  return { skipped, written };
}

/** Op sets alone, the shape every opening writer states. */
export function applyStructureOps(
  structureId: string,
  opsOrList: StructureOpsByKey | StructureOpsByKey[]
): Promise<ApplyOpsResult> {
  const list = Array.isArray(opsOrList) ? opsOrList : [opsOrList];
  return applyStructureWrite(
    structureId,
    list.map((ops) => ({ ops }))
  );
}
